'use client';

import { type ReactElement, useState, useEffect, useCallback, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { cn } from '@fshr/shared-react';
import { formatPrice } from '@fshr/shared';
import { useLocale } from '@fshr/context/i18n-context';
import { useTealium } from '@fshr/context/tealium-context';
import BevSafeText from '../bev-safe-text';
import CancellationPolicyModal from '../cancellation-policy-modal';
import { isUnavailableForStay } from '../unavailable-booking-widget/availability';
import type { Props } from './types';

const DEFAULT_FROM_LABEL = 'From';
const DEFAULT_RESERVE_NOW_LABEL = 'Reserve Now';
const DEFAULT_CANCELLATION_POLICY_LABEL = 'Cancellation Policy';
const DEFAULT_BOOKING_WIDGET_SELECTOR = '[data-booking-widget]';
const PREVIEW_HEIGHT_VARIABLE = '--booking-widget-preview-height';

function BookingWidgetPreview({
	productId,
	productAvailabilityStore,
	fromLabel = DEFAULT_FROM_LABEL,
	reserveNowLabel = DEFAULT_RESERVE_NOW_LABEL,
	cancellationPolicyLabel = DEFAULT_CANCELLATION_POLICY_LABEL,
	cancellationPolicyContent,
	bookingWidgetSelector = DEFAULT_BOOKING_WIDGET_SELECTOR,
	onCancellationPolicyClick,
	onOpenFullWidget,
}: Props): ReactElement | null {
	const locale = useLocale();
	const { trackInteraction } = useTealium();
	const [isVisible, setIsVisible] = useState(true);
	const [isCancellationModalOpen, setIsCancellationModalOpen] = useState(false);
	const previewRef = useRef<HTMLDivElement>(null);

	const { fetching, allProducts } = productAvailabilityStore;
	const product = allProducts.find(p => p.id === productId);
	const summaryPrice = product?.summaries?.price;
	const unavailableForStay = isUnavailableForStay(productAvailabilityStore, productId);

	useEffect(() => {
		let io: IntersectionObserver | null = null;
		let mo: MutationObserver | null = null;

		function watchTarget(target: Element) {
			io = new IntersectionObserver(
				([entry]) => {
					setIsVisible(!entry.isIntersecting);
				},
				{ threshold: 0 }
			);
			io.observe(target);
		}

		const target = document.querySelector(bookingWidgetSelector);
		if (target) {
			watchTarget(target);
		} else {
			// Target not yet in the DOM (e.g. BookingWidget stores still loading) —
			// wait for it with a MutationObserver and attach the IntersectionObserver
			// once it appears.
			mo = new MutationObserver(() => {
				const t = document.querySelector(bookingWidgetSelector);
				if (t) {
					mo?.disconnect();
					mo = null;
					watchTarget(t);
				}
			});
			mo.observe(document.body, { childList: true, subtree: true });
		}

		return () => {
			io?.disconnect();
			mo?.disconnect();
		};
	}, [bookingWidgetSelector]);

	useEffect(() => {
		const root = document.documentElement;
		const element = previewRef.current;

		if (!element || !isVisible) {
			root.style.setProperty(PREVIEW_HEIGHT_VARIABLE, '0px');
			return;
		}

		const publishHeight = () => {
			root.style.setProperty(
				PREVIEW_HEIGHT_VARIABLE,
				`${Math.round(element.getBoundingClientRect().height)}px`
			);
		};

		publishHeight();

		const resizeObserver = new ResizeObserver(publishHeight);
		resizeObserver.observe(element);

		return () => {
			resizeObserver.disconnect();
			root.style.setProperty(PREVIEW_HEIGHT_VARIABLE, '0px');
		};
	}, [isVisible, fetching, product, summaryPrice, unavailableForStay]);

	const handleReserveNow = useCallback(() => {
		trackInteraction('product detail page', 'booking widget banner', 'reserve now');

		const target = document.querySelector(bookingWidgetSelector);
		const isMobile = window.innerWidth < 960;

		if (isMobile && onOpenFullWidget) {
			onOpenFullWidget();
		} else if (target) {
			target.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, [bookingWidgetSelector, onOpenFullWidget, trackInteraction]);

	if (
		fetching ||
		!product ||
		!summaryPrice ||
		unavailableForStay ||
		product.hidden ||
		(product.category != null && !product.category.cartEnabled)
	)
		return null;

	// Use the prop if provided; fall back to product.terms from the store so the
	// link shows even when the parent template doesn't pass cancellationPolicyContent.
	const effectiveCancellationContent =
		cancellationPolicyContent ?? (product as { terms?: string }).terms;

	const handleCancellationPolicyClick = onCancellationPolicyClick
		? onCancellationPolicyClick
		: () => setIsCancellationModalOpen(true);

	const isComplimentary = summaryPrice.complimentary;
	const priceText = isComplimentary
		? 'Complimentary'
		: `${fromLabel} ${formatPrice({
				value: summaryPrice.from?.amount,
				currencyCode: summaryPrice.from?.currencyCode,
				locale,
				decimalPlaces: 0,
			})}`;
	const showPerPerson = !isComplimentary && summaryPrice.from?.type === 'PER_PERSON';

	return (
		<>
			<div
				ref={previewRef}
				className={cn(
					'fixed bottom-0 left-0 right-0 z-50',
					'lg:left-auto lg:right-[90px] lg:w-[507px]',
					'bg-surface-raised',
					'border-t border-border-subtle',
					'rounded-t-[20px]',
					'shadow-[0_10px_25px_rgba(3,3,3,0.2)] lg:hover:shadow-[0_10px_25px_rgba(3,3,3,0.35)]',
					'transition-opacity duration-150',
					isVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
				)}
				data-testid="booking-widget-preview"
			>
				<div className="flex items-center gap-200 px-card-padding-s pb-600 pt-card-padding-xs lg:pb-card-padding-xs">
					<div className="flex min-w-0 flex-1 flex-col gap-y-stack-2xs">
						{/* Figma "Frame 14706" (price to /person) and its parent
						    "Paragraph" are both bound to stack-2xs. */}
						<div className="flex flex-col gap-y-stack-2xs">
							<span className="text-subtitle2 text-text-heading">{priceText}</span>
							{showPerPerson && <span className="text-caption text-text-secondary">/person</span>}
						</div>
						{effectiveCancellationContent && (
							<button
								type="button"
								onClick={handleCancellationPolicyClick}
								className="self-start text-caption lg:text-[14px] text-text-primary underline underline-offset-2"
							>
								<BevSafeText>{cancellationPolicyLabel}</BevSafeText>
							</button>
						)}
					</div>

					<button
						type="button"
						onClick={handleReserveNow}
						className={
							cn(
								'inline-flex h-400 shrink-0 items-center justify-center',
								'min-w-1000 px-200',
								'rounded-sm',
								'bg-button-primary text-button-primary-inverse',
								'transition-colors hover:bg-button-primary-hover',
								'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus'
							) + ' text-button2'
						}
					>
						{reserveNowLabel}
					</button>
				</div>
			</div>

			{effectiveCancellationContent && !onCancellationPolicyClick && (
				<CancellationPolicyModal
					isOpen={isCancellationModalOpen}
					onClose={() => setIsCancellationModalOpen(false)}
					title={cancellationPolicyLabel}
					cancellationPolicy={effectiveCancellationContent}
				/>
			)}
		</>
	);
}

export default observer(BookingWidgetPreview);
